"use client"

import {UiErrorText} from "@/components/i18n/ui-error-text"
import { useCallback, type ReactNode } from "react"
import { useSearchParams } from "next/navigation"

import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  useConversationOutcomesQuery,
  useConversationServiceMetricsQuery,
} from "@/hooks/queries/conversation-quality"
import { totalOutcomes } from "@/lib/conversation-quality/metric-definitions"
import type { QUALITY_TAB_LANDINGS } from "@/lib/conversation-quality/page-tabs"
import type {
  ConversationOutcomesResult,
  QualityPeriodInput,
  ServiceMetricsResult,
} from "@/server-actions/conversation-quality"
import { useTranslations } from "next-intl"


/** Whether the feature runs: while `loading` its sections are skeletons, while `off` they are left out. */
export type QualityState = "loading" | "on" | "off"

export interface QualityTabProps {
  input: QualityPeriodInput;
  /** The profile's, for what does not follow the period; `input.timezone` reads a report's week in UTC. */
  viewerTimezone: string;
  /** False until the profile timezone is known: firing on the "UTC" default would run every aggregate twice. */
  ready: boolean;
  quality: QualityState;
}

/** What a link set for this tab to land on, and its release once landed — a reload lands no second time. */
export function useLanding(param: (typeof QUALITY_TAB_LANDINGS)[number]): [string | null, () => void] {
  const value = useSearchParams().get(param)
  const landed = useCallback(() => {
    const url = new URL(window.location.href)
    url.searchParams.delete(param)
    window.history.replaceState(null, "", url.toString())
  }, [param])
  return [value, landed]
}

/**
 * The outcomes and the service metrics, which Overview and Handovers & team both read under the
 * same keys. Both count only sessions that closed, so a period of open sessions reads as empty
 * here while Volume already counts it.
 */
export function useFinishedSessions({ input, ready, quality }: QualityTabProps) {
  const { data: service, error: serviceError } = useConversationServiceMetricsQuery(input, ready)
  const { data: outcomes, error: outcomesError } = useConversationOutcomesQuery(
    input,
    ready && quality === "on"
  )

  return {
    service,
    serviceError,
    outcomes,
    outcomesError,
    isEmpty: noFinishedSessions(service, outcomes, quality),
  }
}

function noFinishedSessions(
  service: ServiceMetricsResult | undefined,
  outcomes: ConversationOutcomesResult | undefined,
  quality: QualityState
): boolean {
  if (!service || service.current.escalations.sessions > 0) return false

  switch (quality) {
    case "loading":
      return false
    case "off":
      return true
    case "on":
      return outcomes !== undefined && judgedSessions(outcomes) === 0
    default: {
      const exhaustive: never = quality
      return exhaustive
    }
  }
}

function judgedSessions(outcomes: ConversationOutcomesResult): number {
  return (
    Object.values(totalOutcomes(outcomes.current)).reduce((sum, count) => sum + count, 0) +
    outcomes.teamStarted
  )
}

export function NoFinishedSessionsNotice() {
  const tUi = useTranslations("ConversationQuality.ConversationQualityTabsTabParts")

  return <Notice><span key={tUi("noFinishedSessionsInThisPeriodYet")}>{tUi("noFinishedSessionsInThisPeriodYet")}</span></Notice>
}

export function Notice({ children }: { children: ReactNode }) {
  return (
    <Alert>
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  )
}

export function ErrorAlert({ error }: { error: Error }) {
  return (
    <Alert variant="destructive">
      <AlertDescription><UiErrorText message={error.message} /></AlertDescription>
    </Alert>
  )
}
